/**
 * Bound Record Store
 *
 * Central store for all local changes to bound records.
 * This is the single source of truth for uncommitted changes across all shapes
 * bound to the same data record.
 *
 * Architecture:
 * - Stored in Yjs Map: "boundRecordState"
 * - Key: "sourceId:recordId"
 * - Value: BoundRecordState (localChanges, metadata)
 *
 * Benefits:
 * - Single source of truth (no per-shape localChanges sync)
 * - All shapes bound to same record automatically see changes
 * - Repository panel shows correct effective values
 * - Efficient: one write, many reactive reads
 */

import type * as Y from 'yjs';

/**
 * State for a single bound record in the central store
 */
export interface BoundRecordState {
  /** Local changes that haven't been committed to the data source */
  localChanges: Record<string, unknown>;
  /** When the changes were last modified */
  changedAt: string;
  /** User who made the last change */
  changedBy: string;
}

/**
 * Generate the key for a bound record in the store
 */
export function getBoundRecordKey(sourceId: string, recordId: string): string {
  return `${sourceId}:${recordId}`;
}

/**
 * Parse a bound record key back to sourceId and recordId
 */
export function parseBoundRecordKey(key: string): { sourceId: string; recordId: string } | null {
  const colonIndex = key.indexOf(':');
  if (colonIndex === -1) return null;
  return {
    sourceId: key.substring(0, colonIndex),
    recordId: key.substring(colonIndex + 1),
  };
}

/**
 * Get the bound record state map from a Yjs document
 */
export function getBoundRecordStateMap(doc: Y.Doc): Y.Map<BoundRecordState> {
  return doc.getMap('boundRecordState') as Y.Map<BoundRecordState>;
}

/**
 * Get the current state for a bound record
 */
export function getBoundRecordState(
  doc: Y.Doc,
  sourceId: string,
  recordId: string
): BoundRecordState | undefined {
  const map = getBoundRecordStateMap(doc);
  const key = getBoundRecordKey(sourceId, recordId);
  return map.get(key);
}

/**
 * Get the effective value for a field (localChanges override cachedRecord)
 */
export function getEffectiveFieldValue(
  doc: Y.Doc,
  sourceId: string,
  recordId: string,
  cachedRecord: Record<string, unknown>,
  field: string
): unknown {
  const state = getBoundRecordState(doc, sourceId, recordId);
  if (state?.localChanges && field in state.localChanges) {
    return state.localChanges[field];
  }
  return cachedRecord[field];
}

/**
 * Get all effective values for a record (merged cachedRecord + localChanges)
 */
export function getEffectiveRecord(
  doc: Y.Doc,
  sourceId: string,
  recordId: string,
  cachedRecord: Record<string, unknown>
): Record<string, unknown> {
  const state = getBoundRecordState(doc, sourceId, recordId);
  if (!state?.localChanges || Object.keys(state.localChanges).length === 0) {
    return cachedRecord;
  }
  return {
    ...cachedRecord,
    ...state.localChanges,
  };
}

/**
 * Update local changes for a bound record
 * This is the primary mutation function - all bound shape edits should use this
 */
export function updateBoundRecordChanges(
  doc: Y.Doc,
  sourceId: string,
  recordId: string,
  fieldChanges: Record<string, unknown>,
  cachedRecord: Record<string, unknown>,
  userId: string
): void {
  const map = getBoundRecordStateMap(doc);
  const key = getBoundRecordKey(sourceId, recordId);
  const current = map.get(key);

  // Merge with existing local changes
  const newLocalChanges: Record<string, unknown> = {
    ...current?.localChanges,
    ...fieldChanges,
  };

  // Remove fields that match the cached record (no actual change)
  for (const [field, value] of Object.entries(newLocalChanges)) {
    if (cachedRecord[field] === value) {
      delete newLocalChanges[field];
    }
  }

  // If no changes remain, delete the entry
  if (Object.keys(newLocalChanges).length === 0) {
    doc.transact(() => {
      map.delete(key);
    }, 'bound-record-clear');
    return;
  }

  // Update the store
  doc.transact(() => {
    map.set(key, {
      localChanges: newLocalChanges,
      changedAt: new Date().toISOString(),
      changedBy: userId,
    });
  }, 'bound-record-update');
}

/**
 * Set multiple field changes at once (batch update)
 */
export function setBoundRecordFields(
  doc: Y.Doc,
  sourceId: string,
  recordId: string,
  fields: Record<string, unknown>,
  cachedRecord: Record<string, unknown>,
  userId: string
): void {
  updateBoundRecordChanges(doc, sourceId, recordId, fields, cachedRecord, userId);
}

/**
 * Discard all local changes for a specific record
 */
export function discardBoundRecordChanges(
  doc: Y.Doc,
  sourceId: string,
  recordId: string
): void {
  const map = getBoundRecordStateMap(doc);
  const key = getBoundRecordKey(sourceId, recordId);

  doc.transact(() => {
    map.delete(key);
  }, 'bound-record-discard');
}

/**
 * Discard all local changes for a data source
 */
export function discardAllBoundRecordChanges(
  doc: Y.Doc,
  sourceId: string
): void {
  const map = getBoundRecordStateMap(doc);
  const prefix = `${sourceId}:`;

  doc.transact(() => {
    const keysToDelete: string[] = [];
    map.forEach((_, key) => {
      if (key.startsWith(prefix)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => map.delete(key));
  }, 'bound-record-discard-all');
}

/**
 * Get all uncommitted records for a data source
 */
export function getUncommittedRecords(
  doc: Y.Doc,
  sourceId: string
): Array<{ recordId: string; state: BoundRecordState }> {
  const map = getBoundRecordStateMap(doc);
  const prefix = `${sourceId}:`;
  const results: Array<{ recordId: string; state: BoundRecordState }> = [];

  map.forEach((state, key) => {
    if (key.startsWith(prefix)) {
      const recordId = key.substring(prefix.length);
      results.push({ recordId, state });
    }
  });

  return results;
}

/**
 * Check if a record has uncommitted changes
 */
export function hasUncommittedChanges(
  doc: Y.Doc,
  sourceId: string,
  recordId: string
): boolean {
  const state = getBoundRecordState(doc, sourceId, recordId);
  return state !== undefined && Object.keys(state.localChanges).length > 0;
}

/**
 * Get count of uncommitted records for a data source
 */
export function getUncommittedCount(doc: Y.Doc, sourceId: string): number {
  return getUncommittedRecords(doc, sourceId).length;
}

/**
 * After committing changes, clear the local changes and update cached records
 * This should be called after successfully writing to the data source
 */
export function markRecordsAsCommitted(
  doc: Y.Doc,
  sourceId: string,
  committedRecordIds: string[]
): void {
  const map = getBoundRecordStateMap(doc);

  doc.transact(() => {
    for (const recordId of committedRecordIds) {
      const key = getBoundRecordKey(sourceId, recordId);
      map.delete(key);
    }
  }, 'bound-record-committed');
}
